import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {merge, Observable, Subject, Subscription} from 'rxjs';
import {DialogService, UserSessionService} from '@rbaip/ui-component';
import {FormControl} from '@angular/forms';
import {map, startWith, takeUntil} from 'rxjs/operators';
import {ItemSummaryEventServiceImpl} from '../../shared/auction-event/item-summary-event-impl.service';
import {ItemSummaryEventService} from '../../shared/auction-event/item-summary-event.service';
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";

import {
  ASSET_STATUS_CONTRACT_READY,
  ASSET_STATUS_CONTRACTED,
  ASSET_STATUS_INFO_COMPLETE,
  assetInspectionTypeName,
  assetPlatform,
  AssetSaleInfo,
  assetSaleTypeName,
  BUY_NOW,
  consigmentPlatformOptions,
  consigmentSaleTypeOptions,
  E_RESERVE,
  equipmentState,
  FEATURED,
  FULL_INSPECTION,
  INVENTORY_CONTROL,
  ipAssetInspectionTypes,
  ironPlanetSaleTypeOptions,
  livePlatformOptions,
  MAKE_OFFER,
  multiChannelSaleTypeOptions,
  onlinePlatformOptions,
  PlatformTypeOption,
  RBA_LIVE,
  rbAssetInspectionTypes,
  rbSaleTypeOptions,
  SaleTypeOption,
  SALVAGESALE,
  SEALED_BID_SELLER_BID_SELECT,
  SEALED_BID_UNRESERVED,
  SELLER_BID_SELECT,
  SellerLocation,
  YARD_INSPECTION
} from '../../shared/domain/item';
import {AuctionEvent, AuctionEventSchedulingStatus} from '../../shared/domain/auction-event';
import {Site} from '../../shared/domain/site';
import {Address, AddressFormComponent, Contact} from '@rbaip/address-form';
import {ItemSummaryOpportunityService} from '../../shared/opportunity/item-summary-opportunity.service';
import {ItemSummaryAssetControlGroupService} from '../../shared/asset-package/item-summary-asset-control-group.service';
import {appMessage} from '../../shared/app.message';
import {Opportunity, OpportunityType} from '../../shared/domain/opportunity';
import {ContractType, SALESHUB_CONTRACT_CHANNEL} from '../../shared/domain/contract';
import {permissionType, PermissionUtils} from '../../shared/permission';
import {Moment} from 'moment';
import {appConfig} from '../../shared/app.config';
import {ItemSummarySiteListService} from '../../shared/site/item-summary-site-list.service';
import {ItemSummarySiteListServiceImpl} from '../../shared/site/item-summary-site-list-impl.service';
import {MoveIndicatorControlService} from "../../shared/reload-indicator/move-indicator-control.service";
import {AssetSaleFieldInfoTooltip} from "../../shared/domain/item/asset-sale-field-info-tooltip.enum";
import {ITEM_SUMMARY_EXTERNAL_CONFIG_TOKEN} from "../../shared/domain";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {StorageLocationComponent} from "../storage-location-details/storage-location.component";
import {getCurrencyCodeAndSymbol} from '../../shared/currencyUtil';

@Component({
  selector: 'lib-item-summary-asset-sale-info',
  templateUrl: './asset-sale-info.component.html',
  styleUrls: ['./asset-sale-info.component.scss'],
  providers: [{provide: ItemSummaryEventService, useClass: ItemSummaryEventServiceImpl},
              {provide: ItemSummarySiteListService, useClass: ItemSummarySiteListServiceImpl}]
})
export class AssetSaleInfoComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input() assetSaleInfo: AssetSaleInfo;
  @Input() currentDefaultSaleInfo: AssetSaleInfo;
  @Input() displayInAssetSummary: boolean;
  @Input() saveAndCreateAnotherAsset: boolean;
  @Input() enableSaveLocationOnModelSave: boolean;

  @ViewChild('saleType') public saleTypeElement : ElementRef;
  @ViewChild('auctionEvent') public auctionEventElement : ElementRef;
  @ViewChild('storageLocation') public storageLocationElement : ElementRef;
  @ViewChild('reservePrice') public reservePriceElement : ElementRef;
  @ViewChild('addressForm') addressForm : AddressFormComponent;
  @ViewChild('appraisalAddressForm') appraisalAddressForm : AddressFormComponent;

  public saleTypeOptions: SaleTypeOption[] = [];
  public inspectionTypeOptions = ipAssetInspectionTypes;
  public platformOptions: any;
  public auctionEventControl = new FormControl();
  public siteControl = new FormControl();
  public locationControl = new FormControl();
  public appraisalLocationControl = new FormControl();
  public saleTypeControl = new FormControl();
  public platformTypeControl = new FormControl();
  public originalAuctionEvent: AuctionEvent;
  public selectedAuctionEvent: AuctionEvent;
  public filteredAuctionEvent: Observable<AuctionEvent[]>;
  public filteredSiteList: Observable<Site[]>;
  public filteredLocationList: Observable<SellerLocation[]>;
  public filteredAppraisalLocationList: Observable<SellerLocation[]>;
  public filteredSaleTypeList: Observable<SaleTypeOption[]>;
  public filteredPlatformTypeList: Observable<PlatformTypeOption[]>;
  public isVSOEvent = false;
  public assetControlGroupLocations: Array<SellerLocation> = [];
  public siteList: Array<Site> = [];
  public address: Address;
  public appraisalAddress: Address;
  public loadingEvents = false;
  public loadingSites = false;
  public buyNowPercent = 0;
  public yardDeliveryMaxAllowedDate: Date;
  dateFormat = appConfig.dateFormat;
  public readonly FEATURED = FEATURED;
  public readonly E_RESERVE = E_RESERVE;
  public readonly BUY_NOW = BUY_NOW;
  public readonly MAKE_OFFER = MAKE_OFFER;
  public readonly RBA_LIVE = RBA_LIVE;
  public readonly SEALED_BID_UNRESERVED = SEALED_BID_UNRESERVED;
  public readonly SEALED_BID_SELLER_BID_SELECT = SEALED_BID_SELLER_BID_SELECT;
  public readonly SELLER_BID_SELECT = SELLER_BID_SELECT;
  public readonly SALVAGESALE = SALVAGESALE;
  public readonly INVENTORY_CONTROL = INVENTORY_CONTROL;

  public auctionEventServiceSubscription: Subscription;
  public rbAuctionEventListServiceSubscription: Subscription;
  public rbAuctionEvents: Array<AuctionEvent> = [];
  public opportunitySubscription: Subscription;
  public locationListServiceSubscription: Subscription;
  public siteListServiceSubscription: Subscription;
  public siteListControlSubscription: Subscription;
  public auctionEventControlSubscription: Subscription;
  public locationControlSubscription: Subscription;
  public appraisalLocationControlSubscription: Subscription;
  public contact: Contact;
  public eventContact: Contact;
  evenLocationContact: Contact;
  public sellerId: number;
  public opportunity : Opportunity;

  isAtRiskContract = false;
  canUpdateAssetSalesInfo: boolean;
  canEditInspectionType: boolean;
  canEditPrices: boolean;
  canEditShootPrice: boolean;
  canEditYardDeliverydate: boolean;
  canEditStorageLocation: boolean;
  validNumericValue: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  saleTypeChanged$: Subject<string> = new Subject<string>();
  reloadSaleTypes$: Subject<string> = new Subject<string>();
  reloadPlatforms$: Subject<String>  = new Subject<string>();
  private moveIndicatorSubscription: Subscription;
  originalSaleInfo :AssetSaleInfo;
  opportunityCurrency: string;
  public assetSaleInfoToolTips = AssetSaleFieldInfoTooltip;
  isVSOEditable: boolean;
  isEquipSaleInfoFieldsEditable: boolean;
  isEditingContactAndAddress: boolean;
  isEditingAppraisalContactAndAddress: boolean;
  isInspecSchedule: boolean = false;
  isPickUp: boolean = false;

  constructor( public opportunityService: ItemSummaryOpportunityService,
               public assetControlGroupService: ItemSummaryAssetControlGroupService,
               public eventService: ItemSummaryEventService,
               public siteListService: ItemSummarySiteListService,
               public dialogService: DialogService,
               public userSessionService: UserSessionService,
               private moveIndicatorControlService: MoveIndicatorControlService,
               private dialog: MatDialog,
               @Optional() @Inject(ITEM_SUMMARY_EXTERNAL_CONFIG_TOKEN) public appSettings: any) {
    this.subscribeAuctionEvent();
    this.subscribeAuctionEventList();
    this.subscribeLocationList();
    this.subscribeSiteList();
    this.subscribeAuctionEventChange();
    this.subscribeSiteChange();
    this.subscribeLocationChange();
    this.subscribeMoveIndicator();
  }

  ngOnInit() {
    this.loadSaleInfo();
    this.disableFormControls();
    this.isEquipSaleInfoFieldsEditable = this.appSettings && this.appSettings.enableAssetManagement;
    this.canUpdateAssetSalesInfo = PermissionUtils.hasPermission(this.userSessionService, permissionType.canUpdateAssetSalesInfo);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkMissingLocation();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.saveAndCreateAnotherAsset && changes.saveAndCreateAnotherAsset.currentValue) {
      this.clearSaleType(null);
      this.clearPlatformType(null);
      this.cleanAddressInput();
      this.clearSiteInput();
      this.cleanAppraisalAddressInput();
      this.buyNowPercent = 0; /*Initializing buy now percent*/
    }

    if (changes.assetSaleInfo && changes.assetSaleInfo.currentValue) {
      this.originalSaleInfo = changes.assetSaleInfo.currentValue;
      this.setSiteValue(this.originalSaleInfo.siteId);
      if(!this.assetSaleInfo.isAuctionSite){
        this.assetSaleInfo.locationId = this.originalSaleInfo.locationId;
        this.setAddressValue(this.originalSaleInfo.locationId);
      } else{
        this.assetSaleInfo.locationId = null;
        this.setAddressValue(null);
      }

      this.assetSaleInfo.appraisalLocationId = this.originalSaleInfo.appraisalLocationId;
      this.setAppraisalAddressValue(this.originalSaleInfo.appraisalLocationId);
    }
    this.loadSaleInfo();
  }

  ngOnDestroy() {
    if (this.opportunitySubscription) {
      this.opportunitySubscription.unsubscribe();
    }
    this.locationListServiceSubscription.unsubscribe();
    this.unsubscribeAuctionEventChange();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    if (this.moveIndicatorSubscription) {
      this.moveIndicatorSubscription.unsubscribe();
    }
  }

  public isReservePriceValid(): boolean {
    return !(!this.assetSaleInfo.sellerReserveAmt || this.assetSaleInfo.sellerReserveAmt < 1);
  }

  public isShootPriceValid(): boolean {
    if ( this.assetSaleInfo.shootPrice > 0 && this.assetSaleInfo.sellerReserveAmt > 1.2 * this.assetSaleInfo.shootPrice ) {
      return false;
    } else {
      return true;
    }
  }

  public onSaleTypeChange(): void {
    if ( !this.assetSaleInfo.saleType ) {
      return;
    }
    this.updateBySaleType(this.assetSaleInfo.saleType);
  }

  public updateBySaleType(saleTypeCode: string) {
    if (saleTypeCode === RBA_LIVE) {
      this.inspectionTypeOptions = rbAssetInspectionTypes;
      this.assetSaleInfo.inspectionType = YARD_INSPECTION;
      this.assetSaleInfo.isAuctionSite = this.assetSaleInfo.siteId != null;
      this.platformOptions = livePlatformOptions;
    } else {
      this.inspectionTypeOptions = ipAssetInspectionTypes;
      this.assetSaleInfo.inspectionType = FULL_INSPECTION;
      this.assetSaleInfo.isAuctionSite = this.assetSaleInfo.siteId != null;
      this.platformOptions = onlinePlatformOptions;
      this.addSellerBidSelectDefaults();
      this.setAuctionEventValue(undefined);
    }
    this.loadPlatformValue(saleTypeCode);
  }

  private loadPlatformValue(saleTypeCode: string) : void {
    this.platformOptions = saleTypeCode === RBA_LIVE || (saleTypeCode === INVENTORY_CONTROL && this.assetSaleInfo.isRBRealm) ? livePlatformOptions : onlinePlatformOptions;
    if (!this.assetSaleInfo.platformName && saleTypeCode === RBA_LIVE || (saleTypeCode === INVENTORY_CONTROL && this.assetSaleInfo.isRBRealm)) {
      this.assetSaleInfo.platformName =  assetPlatform.RBA;
    }
    const platformOptions = this.findPlatformOptionByCode(this.assetSaleInfo.platformName, this.platformOptions);
    if (platformOptions) {
      this.platformTypeControl.setValue(platformOptions, {emitEvent: false});
    } else {
      this.assetSaleInfo.platformName = null;
      this.platformTypeControl.setValue('', {emitEvent: false});
    }
    this.saleTypeChanged$.next(platformOptions ? platformOptions.name : '');
    this.checkMissingLocation();
  }

  private updateBySelectedSaleType(saleTypeCode: string) {
    if (saleTypeCode === RBA_LIVE) {
      this.inspectionTypeOptions = rbAssetInspectionTypes;
      this.assetSaleInfo.inspectionType = YARD_INSPECTION;
      this.assetSaleInfo.isAuctionSite = true;
      this.platformOptions = livePlatformOptions;
    } else {
      this.inspectionTypeOptions = ipAssetInspectionTypes;
      this.assetSaleInfo.inspectionType = FULL_INSPECTION;
      this.assetSaleInfo.isAuctionSite = false;
      this.platformOptions = onlinePlatformOptions;
      this.addSellerBidSelectDefaults();
      this.setAuctionEventValue(undefined);
    }
    this.loadPlatformValue(saleTypeCode);
  }

  addSellerBidSelectDefaults(){
    if (this.assetSaleInfo.isSellerBidSelect()) {
      this.assetSaleInfo.strikePriceAmt = 1000001;
      this.assetSaleInfo.rejectPriceAmt = 100;
      this.assetSaleInfo.showBuyNowYn = false;
      this.assetSaleInfo.doNotShowAsk = true;
    }
  }

  private findPlatformOptionByCode(platformCode: string , platformOptions: PlatformTypeOption[]): PlatformTypeOption {
    for (const platformOption  of platformOptions) {
        if (platformOption.code === platformCode) {
          return platformOption;
        }
    }
    return null;
  }

  public getAuctionEventName(auctionEvent: AuctionEvent) {
    if (auctionEvent) { return auctionEvent.displayName; }
  }

  public getSiteName(site: Site) {
    if (site) { return site.name; }
  }

  public getLocationName(assetLocation: SellerLocation) {
    if (assetLocation) { return assetLocation.oneLineFullAddress; }
  }

  public isLiveAuction(): boolean {
    return RBA_LIVE === this.assetSaleInfo.saleType;
  }

  public onAuctionEventChange( auctionEvent ): void {
    this.assetSaleInfo.auctionEventLocationType = auctionEvent.locationType;
    if (auctionEvent == null || typeof auctionEvent === 'string') {
      this.assetSaleInfo.yardDeliveyDate = null;
      return;
    }

    if (auctionEvent.localEventEndDateTime) {
      this.yardDeliveryMaxAllowedDate = new Date(auctionEvent.localEventEndDateTime);
    }

    const nowInMillis = new Date().getTime();
    this.assetSaleInfo.auctionEventId = auctionEvent.auctionEventId;
    if (auctionEvent.localEventEndDateTime < nowInMillis) {
      const confirmPastEvent =  appMessage.confirmPastEventSelection;
      this.dialogService.showMessageDialog(confirmPastEvent)
        .afterClosed().subscribe(result => {
        if (result === 'OK') {
          if (this.siteList) {
            const filteredSites = this.siteList.filter(site => site.id == auctionEvent.siteId );
            this.setSiteValue(filteredSites[0]);
          }
          this.setLocationIfOffSiteEvent(auctionEvent);
          this.isVSOEvent = auctionEvent.allowsIpInspection;
          this.calculateYardDeliveryDate(auctionEvent);
        } else {
          // set auctionEvent Form Control value to null
          this.setAuctionEventValue( undefined );
          this.assetSaleInfo.siteId = null;
          this.setSiteValue('');
          this.isVSOEvent = false;
          this.assetSaleInfo.offSite = false;
        }
      });
    } else {
      this.setLocationIfOffSiteEvent(auctionEvent);
      this.isVSOEvent = auctionEvent.allowsIpInspection;
      this.calculateYardDeliveryDate(auctionEvent);
    }

    if (this.assetSaleInfo.siteId && !(auctionEvent.locationType && auctionEvent.locationType === 'Offsite')) {
      this.assetSaleInfo.isAuctionSite = true;
      this.onSiteChange( this.assetSaleInfo.siteId );
    }

    if(!this.isVSOEvent && (!this.assetSaleInfo.equipId ||
      (this.assetSaleInfo.equipmentStatus && this.assetSaleInfo.equipmentStatus < equipmentState.inspectionPerformed))) {
      this.assetSaleInfo.inspectionType = YARD_INSPECTION;
    }
    this.isVSOEditable = !this.assetSaleInfo.isPostContract || (this.assetSaleInfo.isPostContract && this.isVSOEvent) || (this.assetSaleInfo.saleType !== RBA_LIVE);
    this.canEditStorageLocation = !this.assetSaleInfo.isPostContract ||
      (this.isVSOEditable || this.isEquipSaleInfoFieldsEditable) && this.canUpdateAssetSalesInfo;
  }

  public  setLocationIfOffSiteEvent(auctionEvent) {
    if ((auctionEvent.locationType && auctionEvent.locationType === 'Offsite') ) {
      this.checkIfEventLocationExists(auctionEvent);
    } else {
      this.assetSaleInfo.siteId = auctionEvent.siteId;
    }
  }

  checkIfEventLocationExists(auctionEvent) {
    if (this.assetControlGroupLocations) {
      this.assetSaleInfo.siteId = null;
      this.assetSaleInfo.isAuctionSite = false;
      this.setSiteValue('');
      const eventLocations = this.assetControlGroupLocations.filter(location => location.auctionEventId && location.auctionEventId === auctionEvent.auctionEventId);
      if (eventLocations && eventLocations.length > 0) {
        const eventLocation = eventLocations[0];
        this.assetSaleInfo.addNewAddress = false;
        this.assetSaleInfo.locationId = eventLocation.id;
        this.setAddressValue(eventLocation);
      } else {
        this.assetSaleInfo.addNewAddress = true;
        if (this.assetSaleInfo.addNewAddress && ! this.assetSaleInfo.newLocation) {
          this.assetSaleInfo.newLocation = new SellerLocation();
          this.assetSaleInfo.newLocation.locationDesc = auctionEvent.eventLocationContactDto.city;
          this.assetSaleInfo.newLocation.country = auctionEvent.eventLocationContactDto.country;
          this.assetSaleInfo.newLocation.auctionEventId = auctionEvent.auctionEventId;
          this.eventContact = Object.assign(new Address(), JSON.parse(JSON.stringify(auctionEvent.eventLocationContactDto)));
          this.eventContact.cntctId = null;
          this.assetSaleInfo.locationId = null;
          this.consumeAddressChanged(this.eventContact);
          this.setAddressValue(null);
          this.assetSaleInfo.newLocation.sellerId = null; // we dont want to create this location under seller
        }
      }
    }
  }


  public onLocationChange(selectedLocationId: number): void {
    const filteredLocation = this.assetControlGroupLocations.filter(location => location.id === selectedLocationId);
    const selectedLocation = filteredLocation[0];
    if (selectedLocation) {
     if (selectedLocation.activeYn && selectedLocation.activeYn === 'Y') {
       this.assetSaleInfo.locationId = selectedLocation.id;
       this.setAddressValue(selectedLocation);
       this.assetSaleInfo.addNewAddress = false;
       this.checkMissingLocation();
       if (this.auctionEventControl.value && this.assetSaleInfo.auctionEventId && this.rbAuctionEvents) {
         let filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId);
         if (filteredEvents && filteredEvents.length > 0) {
           let eventLocation =  filteredEvents[0].eventLocationContactDto;
           this.assetSaleInfo.offSite = !this.checkIfAssetIsOffSite(null, eventLocation, selectedLocation.addressDto);
         }
       }
     } else {
       const confirmInactiveLocation = appMessage.confirmInactiveLocation;
       this.dialogService.showMessageDialog(confirmInactiveLocation)
         .afterClosed().subscribe(result => {
         if (result === 'OK') {
           this.assetSaleInfo.locationId = null;
           this.setAddressValue(null);
           this.assetSaleInfo.addNewAddress = false;
           this.assetControlGroupLocations = this.assetControlGroupLocations.filter(location => location.id != selectedLocationId );
         }
       });
     }
    }
  }

  public onAppraisalLocationChange(selectedLocationId: number): void {
    const filteredLocation = this.assetControlGroupLocations.filter(location => location.id === selectedLocationId);
    const selectedLocation = filteredLocation[0];
    if (selectedLocation) {
      if (selectedLocation.activeYn && selectedLocation.activeYn === 'Y') {
        this.assetSaleInfo.appraisalLocationId = selectedLocation.id;
        this.setAppraisalAddressValue(selectedLocation);
        this.assetSaleInfo.addNewAppraisalAddress = false;
      } else {
        const confirmInactiveLocation = appMessage.confirmInactiveLocation;
        this.dialogService.showMessageDialog(confirmInactiveLocation)
          .afterClosed().subscribe(result => {
          if (result === 'OK') {
            this.assetSaleInfo.appraisalLocationId = null;
            this.setAppraisalAddressValue(null);
            this.assetSaleInfo.addNewAppraisalAddress = false;
            this.assetControlGroupLocations = this.assetControlGroupLocations.filter(location => location.id != selectedLocationId );
          }
        });
      }
    }
  }

  public onSiteChange(selectedSite: number): void {
    const filteredSites = this.siteList.filter(site => site.id == selectedSite);
    if (filteredSites && filteredSites.length > 0) {
      const siteLocation = new SellerLocation();
      siteLocation.addressDto = filteredSites[0].address;
      siteLocation.siteId = filteredSites[0].id;
      siteLocation.locationDesc = filteredSites[0].name;
      this.assetSaleInfo.siteLocation = siteLocation;
      this.assetSaleInfo.assetLocationCountry = this.assetSaleInfo.siteLocation.addressDto.country;
      this.setSiteValue(filteredSites[0]);
      this.assetSaleInfo.siteId = siteLocation.siteId;
      this.assetSaleInfo.locationId = null;
      if (this.auctionEventControl.value && this.assetSaleInfo.auctionEventId && this.rbAuctionEvents) {
        let filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId);
        if (filteredEvents && filteredEvents.length > 0) {
          let eventLocation =  filteredEvents[0].eventLocationContactDto;
          this.assetSaleInfo.offSite = !this.checkIfAssetIsOffSite(siteLocation.addressDto, eventLocation, null);
        }
      }
    }
  }

  public checkIfAssetIsOffSite(siteContact: Address, auctionEventContact : Address, selectedLocationContact: Address): boolean{
    if(auctionEventContact){
      if(siteContact){
        return this.isEventLocationIsSameAsAssetLocation(auctionEventContact, siteContact);
      } else if(selectedLocationContact) {
        return this.isEventLocationIsSameAsAssetLocation(auctionEventContact, selectedLocationContact);
      }
    }
  }

  public isEqualsString(s1: string, s2:string) : boolean{
    return  (s1 == null && s2 == null) ||
      (s1 != null && s2 != null && ((s1==="" && s2==="") ||
        (s1===s2)));
  }

  public isEventLocationIsSameAsAssetLocation( eventLocation: Address, assetLocation: Address){
    if(eventLocation  && assetLocation){
      return this.isEqualsString(eventLocation.city, assetLocation.city) &&
        this.isEqualsString(eventLocation.province, assetLocation.province) &&
        this.isEqualsString(eventLocation.postcode, assetLocation.postcode) &&
        this.isEqualsString(eventLocation.country, assetLocation.country) &&
        this.isEqualsString(eventLocation.address, assetLocation.address);
    }

  }
  public toggleNewAddressForm(): void {
    this.showStorageLocationMessage();
    this.assetSaleInfo.addNewAddress = !this.assetSaleInfo.addNewAddress;
    if (this.assetSaleInfo.addNewAddress && ! this.assetSaleInfo.newLocation) {
      this.assetSaleInfo.newLocation = new SellerLocation();
      this.assetSaleInfo.newLocation.sellerId = this.sellerId;
    }
    if(this.assetSaleInfo.saleType !== RBA_LIVE) {
      if (this.assetSaleInfo.addNewAddress) {
        if (this.assetSaleInfo.newLocation && this.checkContactInfo(this.assetSaleInfo.newLocation.inspContact) &&
          this.checkContactInfo(this.assetSaleInfo.newLocation.transportContact)) {
          this.assetSaleInfo.isMissingContact = false;
        } else {
          this.assetSaleInfo.isMissingContact = true;
        }
      } else {
        if (this.assetSaleInfo.newSellerLocation && this.checkContactInfo(this.assetSaleInfo.newSellerLocation.inspContact) &&
          this.checkContactInfo(this.assetSaleInfo.newSellerLocation.transportContact)) {
          this.assetSaleInfo.isMissingContact = false;
        } else {
          this.assetSaleInfo.isMissingContact = true;
        }
      }
    }
  }

  public toggleNewAppraisalAddressForm(): void {
    this.assetSaleInfo.addNewAppraisalAddress = !this.assetSaleInfo.addNewAppraisalAddress;
    if (this.assetSaleInfo.addNewAppraisalAddress && ! this.assetSaleInfo.newAppraisalLocation) {
      this.assetSaleInfo.newAppraisalLocation = new SellerLocation();
      this.assetSaleInfo.newAppraisalLocation.sellerId = this.sellerId;
    }
  }

  public consumeAddressChanged(updatedAddress: Address): void {
    this.contact.updateAddressBasedOnGooglePlacesAddress(updatedAddress);
    this.address = updatedAddress;
    this.assetSaleInfo.newLocation.addressDto = this.address;
    this.assetSaleInfo.newLocation.streetAdd1 = this.address.address;
    this.assetSaleInfo.newLocation.city = this.address.city;
    this.assetSaleInfo.newLocation.prov = this.address.province;
    this.assetSaleInfo.newLocation.postalCode = this.address.postcode;
    this.assetSaleInfo.newLocation.country = this.address.country;
    this.assetSaleInfo.newLocation.sellerId = this.sellerId;
    this.assetSaleInfo.assetLocationCountry = this.address.country;
    this.showStorageLocationMessage();
    if(this.auctionEventControl.value && this.assetSaleInfo.auctionEventId && this.rbAuctionEvents){
      let filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId);
      if (filteredEvents && filteredEvents.length > 0) {
        let eventLocation =  filteredEvents[0].eventLocationContactDto;
        this.assetSaleInfo.offSite = !this.checkIfAssetIsOffSite(null, eventLocation, this.assetSaleInfo.newLocation.addressDto);
      }
    }
  }

  public consumeAppraisalAddressChanged(updatedAddress: Address): void {
    this.appraisalAddress = updatedAddress;
    this.assetSaleInfo.newAppraisalLocation.addressDto = this.appraisalAddress;
    this.assetSaleInfo.newAppraisalLocation.streetAdd1 = this.appraisalAddress.address;
    this.assetSaleInfo.newAppraisalLocation.city = this.appraisalAddress.city;
    this.assetSaleInfo.newAppraisalLocation.prov = this.appraisalAddress.province;
    this.assetSaleInfo.newAppraisalLocation.postalCode = this.appraisalAddress.postcode;
    this.assetSaleInfo.newAppraisalLocation.country = this.appraisalAddress.country;
    this.assetSaleInfo.newAppraisalLocation.sellerId = this.sellerId;
  }

  public showStorageLocationMessage() {
    this.assetSaleInfo.locationChangeWarning = false;
    if ( this.currentDefaultSaleInfo ) {
      if ( this.assetSaleInfo.isAuctionSite != this.currentDefaultSaleInfo.isAuctionSite
        || this.assetSaleInfo.siteId != this.currentDefaultSaleInfo.siteId
        || this.assetSaleInfo.locationId != this.currentDefaultSaleInfo.locationId
        || this.assetSaleInfo.addNewAddress !=  this.currentDefaultSaleInfo.addNewAddress
        || this.assetSaleInfo.newLocation != this.currentDefaultSaleInfo.newLocation ) {
        this.assetSaleInfo.locationChangeWarning = true;
      }
    }
  }

  public onStorageLocationTypeChange(): void {
    this.showStorageLocationMessage();
    if ( this.assetSaleInfo.saleType !== RBA_LIVE ) {
      this.assetSaleInfo.inspectionType = FULL_INSPECTION;
    }
    this.checkMissingLocation();
    this.checkOffSiteLocation();
  }

  public onStorLocationChange(): void {
    this.showStorageLocationMessage();
  }

  public isInspectionTypeVisible(): boolean {
    return ( this.assetSaleInfo.saleType !== RBA_LIVE && this.assetSaleInfo.saleType !== INVENTORY_CONTROL )  || ( this.assetSaleInfo.saleType === RBA_LIVE && this.isVSOEvent );
  }

  public getSaleTypeName(): string {
    return assetSaleTypeName[this.assetSaleInfo.saleType];
  }

  public getPlatformName(): string {
    return this.assetSaleInfo.platformName;
  }

  public getInspectionTypeName(): string {
    return assetInspectionTypeName[this.assetSaleInfo.inspectionType];
  }

  public getSelectedAuctionEventName(): string {
    if (this.assetSaleInfo.saleType !== RBA_LIVE && this.assetSaleInfo.isPostContract && !this.assetSaleInfo.isRBRealm) {
      return this.assetSaleInfo.auctionEventName;
    } if (this.assetSaleInfo.saleType !== RBA_LIVE && this.assetSaleInfo.isPostContract && this.assetSaleInfo.isRBRealm) {
      return ( ( this.originalAuctionEvent ) ?  this.originalAuctionEvent.saleNumber + ' - ' : '' ) + this.assetSaleInfo.auctionEventName;
    } else if (this.selectedAuctionEvent && this.selectedAuctionEvent.auctionEventId != this.assetSaleInfo.auctionEventId
                || ( !this.selectedAuctionEvent && this.assetSaleInfo.auctionEventId)) {
      const filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId);
      if (filteredEvents && filteredEvents.length > 0) {
        this.selectedAuctionEvent = filteredEvents[0];
      }
    }

    return this.getAuctionEventName(this.selectedAuctionEvent);
  }

  public isSelectedAuctionEventCanceled(): boolean {
    return this.rbAuctionEvents
      && this.assetSaleInfo && this.assetSaleInfo.auctionEventId
      && this.rbAuctionEvents.some(auctionEvent => auctionEvent.auctionEventId === this.assetSaleInfo.auctionEventId
      && auctionEvent.schedulingStatus === AuctionEventSchedulingStatus.Cancelled);
  }
  public getSelectedLocationName(): string {
    let locationName;
    if ( this.siteList && ( this.assetSaleInfo.isAuctionSite || ( this.selectedAuctionEvent && !this.selectedAuctionEvent.eventLocationContactDto && this.selectedAuctionEvent.siteId ) )) {
      const selectedSite = this.siteList.filter(site => site.id === this.assetSaleInfo.siteId)[0];
      locationName = selectedSite ? selectedSite.cityProvinceCountry : '';
    } else if ( !this.assetSaleInfo.isAuctionSite && this.assetControlGroupLocations && this.assetControlGroupLocations.length > 0 ) {
      const selectedLocation = this.assetControlGroupLocations.filter(location => location.id === this.assetSaleInfo.locationId);
      if ( selectedLocation && selectedLocation.length > 0 ) {
        locationName = selectedLocation[0].oneLineFullAddress;
      }
    }

    if ( !locationName ) {
      if (this.assetSaleInfo.isAuctionSite && this.assetSaleInfo.siteLocation) {
        locationName = this.assetSaleInfo.siteLocation.addressDto.getCityProvincePostalCountry();
      } else if ( this.assetSaleInfo.newLocation) {
        locationName = this.assetSaleInfo.newLocation.addressDto.getOnelineAddress();
      }
    }
    return locationName;
  }

  public getSelectedAppraisalLocationName(): string {
    let locationName;
    if ( this.assetControlGroupLocations && this.assetControlGroupLocations.length > 0 ) {
      const selectedLocation = this.assetControlGroupLocations.filter(location => location.id === this.assetSaleInfo.appraisalLocationId);
      if ( selectedLocation && selectedLocation.length > 0 ) {
        locationName = selectedLocation[0].oneLineFullAddress;
      }
    }

    if ( !locationName ) {
      if ( this.assetSaleInfo.newAppraisalLocation) {
        locationName = this.assetSaleInfo.newAppraisalLocation.addressDto.getOnelineAddress();
      }
    }
    return locationName;
  }

  public updateBuyNowPercent(buyNowPrice: number): void {
    if (!!this.assetSaleInfo.shootPrice) {
      let percent = buyNowPrice / this.assetSaleInfo.shootPrice;
      if (percent == 0 || percent == 1 || percent == 1.05 || percent == 1.10 || percent == 1.15) {
        this.buyNowPercent = percent;
      }
    }
  }

  public changeBuyNowPrice(): void {
    if (this.assetSaleInfo.shootPrice && (this.assetSaleInfo.saleType == BUY_NOW || this.assetSaleInfo.saleType == E_RESERVE)) {
      this.assetSaleInfo.strikePriceAmt = +(Math.round(((this.buyNowPercent * this.assetSaleInfo.shootPrice) / 100) * 100));
    }
  }

  public calculateReject(): void {
    this.assetSaleInfo.rejectPriceAmt = Math.floor(this.assetSaleInfo.strikePriceAmt * (25 / 100));
    if (this.assetSaleInfo.notifySellerYn) {
      this.assetSaleInfo.notifyPriceAmt = Math.floor(this.assetSaleInfo.strikePriceAmt * (50 / 100));
    }
  }

  public disableBuyNow(): void {
    if (this.assetSaleInfo.doNotShowAsk) {
      this.assetSaleInfo.showBuyNowYn = false;
    }
  }

  public changeNotifySellerYn(): void {
    if (this.assetSaleInfo.notifySellerYn) {
      this.assetSaleInfo.notifyPriceAmt = 0;
    }
  }

  public filterAuctionEventList(auctionEvent: any): AuctionEvent[] {
    let eventNameLower = '';
    let saleNumberLower = '';
    if ( typeof auctionEvent === 'string') {
      eventNameLower =  auctionEvent.toLowerCase();
      saleNumberLower = auctionEvent.toLowerCase();
    } else if (auctionEvent) {
      eventNameLower = auctionEvent.eventName.toLowerCase();
      saleNumberLower = auctionEvent.saleNumber.toLowerCase();
    }

    return this.rbAuctionEvents.filter(event => event.eventName.toLowerCase().includes(eventNameLower) || (event.saleNumber != null && event.saleNumber.toLowerCase().includes(saleNumberLower)));
  }

  public filteredRbaSiteList(siteName: string): Site[] {
    return this.siteList.filter(site => site.name && site.name.toLowerCase().includes(siteName));
  }

  public filteredLocations(locationName: string): SellerLocation[] {
    return this.assetControlGroupLocations
      .filter(location => location.addressDto && location.addressDto.oneLineFullAddress
                        && location.addressDto.oneLineFullAddress.toLowerCase().includes(locationName));
  }
  public filterSaleType(saleType: string | SaleTypeOption): SaleTypeOption[] {
    let input: string = typeof saleType === 'string' ? saleType : saleType.name;
    input = input ? input.trim() : '';
    return this.saleTypeOptions.filter(saleTypeOption =>
      saleTypeOption.name && saleTypeOption.name.toLowerCase().startsWith(input.toLowerCase()));
  }
  public filterPlatformList(platformType: string | PlatformTypeOption): PlatformTypeOption[] {
    let input: string = typeof platformType === 'string' ? platformType : platformType.name;
    input = input ? input.trim() : '';
    if (!this.platformOptions) {
      this.platformOptions = onlinePlatformOptions;
    }
    return this.platformOptions.filter(platformTypeOption =>
      platformTypeOption.name && platformTypeOption.name.toLowerCase().startsWith(input.toLowerCase()));
  }

  public saleTypeSelected(event: MatAutocompleteSelectedEvent) {
    const selectedSaleType: SaleTypeOption = event.option.value;
    if (this.assetSaleInfo.saleType !== selectedSaleType.code) {
      this.assetSaleInfo.saleType = selectedSaleType.code;
      this.platformTypeControl.setValue('');
      this.updateBySelectedSaleType(selectedSaleType.code);
    }
  }
  public clearSaleType(event: any) {
    this.saleTypeControl.setValue('', {emitEvent: event ? true : false});
    this.assetSaleInfo.saleType = null;
    this.updateBySelectedSaleType('');
    this.saleTypeChanged$.next('');
  }
  public saleTypeFocusOut(event: any) {
    const inputedValue = this.saleTypeControl.value;
    if (inputedValue && typeof inputedValue === 'string') {
      this.clearSaleType(null);
    }
  }
  public saleTypeFocusIn(event: any) {
    const inputedValue = this.saleTypeControl.value;
    if (inputedValue && typeof inputedValue === 'string'
    && inputedValue.trim().length === 0) {
      this.reloadSaleTypes$.next('');
    }
  }

  public getSaleTypeDisplayName(selectedSaleType: SaleTypeOption) {
     return selectedSaleType && selectedSaleType.name ? selectedSaleType.name : '';
  }

  public platformTypeSelected(event: MatAutocompleteSelectedEvent) {
    const selectedSaleType: PlatformTypeOption = event.option.value;
    this.assetSaleInfo.platformName = selectedSaleType.code;
  }

  public clearPlatformType(event: any) {
    this.platformTypeControl.setValue('', {emitEvent: event ? true : false});
  }

  public platformFocusOut(event: any) {
    const inputedValue = this.platformTypeControl.value;
    if (inputedValue && typeof inputedValue === 'string') {
      this.clearPlatformType(null);
    }
  }
  public platformFocusIn(event: any) {
    const inputedValue = this.platformTypeControl.value;
    if (inputedValue && typeof inputedValue === 'string'
      && inputedValue.trim().length === 0 || !inputedValue ) {
      this.reloadPlatforms$.next('');
    }
  }
  public getPlatformTypeDisplayName(selectedPlatformType: SaleTypeOption) {
    return selectedPlatformType && selectedPlatformType.name ? selectedPlatformType.name : '';
  }

  public initSaleTypeDropdown(opportunityId): void {
    if (opportunityId) {
      this.opportunitySubscription = this.opportunityService.getOpportunityDetails(opportunityId).subscribe(
        opportunity => {
          this.opportunity = opportunity;
          this.initAddress(opportunity);
          switch (opportunity.opportunityRecordType) {
            case OpportunityType.IRONPLANET:
            case OpportunityType.NON_MARS: {
              this.saleTypeOptions = ironPlanetSaleTypeOptions;
              this.platformOptions = onlinePlatformOptions;
              break;
            }
            case OpportunityType.MULTICHANNEL:
            case OpportunityType.CONFIDENTIAL: {
              this.saleTypeOptions = multiChannelSaleTypeOptions;
              if ( this.assetSaleInfo.saleType === RBA_LIVE ){
                this.platformOptions = livePlatformOptions;
              } if ( this.assetSaleInfo.saleType === INVENTORY_CONTROL && this.assetSaleInfo.isRBRealm ) {
                this.platformOptions = livePlatformOptions;
              } else {
                this.platformOptions = onlinePlatformOptions;
              }
              break;
            } case OpportunityType.CONSIGNMENT_OPPORTUNITY: {
              const contract_channel : string = (this.opportunity.contractChannel) ? this.opportunity.contractChannel : "N/A";
              switch (contract_channel) {
                case SALESHUB_CONTRACT_CHANNEL.online:{
                  this.saleTypeOptions = ironPlanetSaleTypeOptions;
                  this.platformOptions = onlinePlatformOptions;
                  break;
                } case SALESHUB_CONTRACT_CHANNEL.live:{
                  this.saleTypeOptions = rbSaleTypeOptions;
                  this.platformOptions = livePlatformOptions;
                  break;
                } default:{
                  this.saleTypeOptions = consigmentSaleTypeOptions;
                  this.platformOptions = consigmentPlatformOptions;
                }
              }
              break;
            } default: {
              this.saleTypeOptions = rbSaleTypeOptions;
              this.platformOptions = livePlatformOptions;
              if (this.assetSaleInfo.saleType !== INVENTORY_CONTROL) {
                this.assetSaleInfo.saleType = RBA_LIVE;
              }
              this.assetSaleInfo.isAuctionSite = !(this.assetSaleInfo.siteId == null && this.assetSaleInfo.locationId != null);
            }
          }
          this.assetSaleInfo.canModifyEvent = !opportunity ||
            (opportunity.opportunityRecordType !== OpportunityType.ANNUAL && opportunity.opportunityRecordType !== OpportunityType.EXHIBIT_A &&
            opportunity.opportunityRecordType !== OpportunityType.FIXED_ASSETS &&
            opportunity.opportunityRecordType !== OpportunityType.SUSPENSE_ASSETS && opportunity.opportunityRecordType !== OpportunityType.INVENTORY_ASSETS );
          this.opportunityService.selectedOpportunityChanged.next(opportunity);
          this.isAtRiskContract = ContractType.isRiskContract(opportunity.contractRecordType);
          this.sellerId = opportunity.opportunityRecordType === OpportunityType.CONFIDENTIAL ? null : opportunity.sellerId;
          this.loadInspectionTypes();
          this.loadPagePermission();
          this.saleTypeControl.setValue( {name: this.getSaleTypeName(), code: this.assetSaleInfo.saleType} as SaleTypeOption, {onlySelf: true, emitEvent: false});
          this.loadPlatformValue(this.assetSaleInfo.saleType);
          this.subscribeSaleTypeChange();
          this.subscribePlatformTypeChange();
        });
    } else {
      this.saleTypeOptions = multiChannelSaleTypeOptions;
      this.subscribeSaleTypeChange();
      this.subscribePlatformTypeChange();
      this.loadInspectionTypes();
      this.loadPagePermission();
    }
  }

  private initAddress(opportunity: Opportunity) {
    if (opportunity && opportunity.country) {
      this.address = new Address();
      this.address.country = opportunity.country;
    }
  }

  public loadInspectionTypes(): void {
    if ( this.assetSaleInfo.saleType === RBA_LIVE ) {
      this.inspectionTypeOptions = rbAssetInspectionTypes;
      if ( !this.assetSaleInfo.inspectionType ) {
        this.assetSaleInfo.inspectionType = YARD_INSPECTION;
      }
    } else {
      this.inspectionTypeOptions = ipAssetInspectionTypes;
    }
  }

  public categorizeAuctionEvents(rbAuctionEvents: AuctionEvent[]) {
    if ( !rbAuctionEvents ) { return; }

    const nowInMillis = new Date().getTime();
    const months = new Array(12);
    months[0] = 'Jan';
    months[1] = 'Feb';
    months[2] = 'Mar';
    months[3] = 'Apr';
    months[4] = 'May';
    months[5] = 'Jun';
    months[6] = 'Jul';
    months[7] = 'Aug';
    months[8] = 'Sept';
    months[9] = 'Oct';
    months[10] = 'Nov';
    months[11] = 'Dec';

    if(this.opportunity && this.opportunity.operationEntityCountryCode){
      rbAuctionEvents = rbAuctionEvents.filter(auctionEvent  => auctionEvent.eventLocationContactDto && auctionEvent.eventLocationContactDto.country === this.opportunity.operationEntityCountryCode)
    }
    rbAuctionEvents = rbAuctionEvents.filter(auctionEvent => auctionEvent.schedulingStatus !== AuctionEventSchedulingStatus.Cancelled);
    if ( this.originalAuctionEvent ) {
      if ( !rbAuctionEvents.find(auctionEvent => auctionEvent.auctionEventId === this.originalAuctionEvent.auctionEventId) ) {
        rbAuctionEvents.push( this.originalAuctionEvent );
      }
    }

    for ( const auctionEvent of rbAuctionEvents ) {
      if ( auctionEvent.localEventStartDateTime > nowInMillis ) {
        auctionEvent.isPreSale = true;
        auctionEvent.status = 'Pre-Sale';
      } else {
        auctionEvent.isActive = true;
        auctionEvent.status = 'Sale-Day';
        if ( this.assetSaleInfo.displayOnlyFutureEvents ) {
          continue;
        }
      }
      const date = new Date(auctionEvent.localEventStartDateTime);
      const auctionEventStartDt = (date.getMonth() + 1) + '-' + date.getDate() + '-' +  date.getFullYear();
      let saleNumber = '';
      if ( auctionEvent.saleNumber != null ) {
        saleNumber = auctionEvent.saleNumber + ' - ';
      }
      auctionEvent.displayStartDate = date.getDate() + ' ' + months[date.getMonth()]  + ', ' +  date.getFullYear() ;

      const endDate = new Date(auctionEvent.localEventEndDateTime);
      auctionEvent.displayEndDate =  endDate.getDate() + ' ' + months[endDate.getMonth()]  + ', ' +  endDate.getFullYear() ;


      auctionEvent.displayName = saleNumber + auctionEvent.displayStartDate  + ' - ' +  auctionEvent.eventName  ;
      if (this.assetSaleInfo.auctionEventId && this.assetSaleInfo.auctionEventId === auctionEvent.auctionEventId) {
        this.setAuctionEventValue(auctionEvent);
        this.selectedAuctionEvent = auctionEvent;
        this.isVSOEvent = this.selectedAuctionEvent.allowsIpInspection;
        this.isVSOEditable = !this.assetSaleInfo.isPostContract || (this.assetSaleInfo.isPostContract && this.isVSOEvent) || (this.assetSaleInfo.saleType !== RBA_LIVE);
        this.canEditStorageLocation = !this.assetSaleInfo.isPostContract ||
          (this.isVSOEditable || this.isEquipSaleInfoFieldsEditable) && this.canUpdateAssetSalesInfo;

        if (this.assetSaleInfo.isSaleInfoFromACG) {
          if (this.assetSaleInfo.yardDeliveyDate) {
            this.assetSaleInfo.yardDeliveyDate = new Date(this.assetSaleInfo.yardDeliveyDate);
          }
        } else {
          if (this.assetSaleInfo.yardDeliveyDate) {
            this.assetSaleInfo.yardDeliveyDate = new Date(this.assetSaleInfo.yardDeliveyDate);
          } else {
            if (this.assetSaleInfo.recalculateYardDeliveryDate) {
              this.calculateYardDeliveryDate(auctionEvent);
            }
          }
        }
      }
      this.rbAuctionEvents.push(auctionEvent);
    }
    this.assetSaleInfo.rbAuctionEvents = this.rbAuctionEvents;
  }

  public subscribeLocationList() {
    this.locationListServiceSubscription = this.assetControlGroupService.assetControlGroupLocationsCallback.subscribe(
    (response) => {
      this.assetControlGroupLocations = response;
      this.assetControlGroupLocations.forEach(location => {
        location.oneLineFullAddress = location.addressDto ? location.addressDto.oneLineFullAddress : '';
        if (!this.assetSaleInfo.siteId && this.assetSaleInfo.locationId && this.assetSaleInfo.locationId == location.id) {
          this.setAddressValue(location);
          if (this.auctionEventControl.value && this.assetSaleInfo.auctionEventId && this.rbAuctionEvents) {
            let filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId);
            if (filteredEvents && filteredEvents.length > 0) {
              let eventLocation =  filteredEvents[0].eventLocationContactDto;
              this.assetSaleInfo.offSite = !this.checkIfAssetIsOffSite(null, eventLocation, location.addressDto);
            }
          }
        }
        if (this.assetSaleInfo.appraisalLocationId && this.assetSaleInfo.appraisalLocationId == location.id) {
          this.setAppraisalAddressValue(location);
        }
      });
      this.assetControlGroupLocations = this.assetControlGroupLocations.filter( location => location.activeYn == 'Y');
      this.assetSaleInfo.assetControlGroupLocations = this.assetControlGroupLocations;
    });
  }

  public subscribeSiteList() {
    this.siteListServiceSubscription = this.siteListService.siteListCallback.subscribe(
      (response) => {
        this.loadSites(response);
        this.loadingSites = false;
      });
  }

  public subscribeAuctionEvent() {
    this.auctionEventServiceSubscription = this.eventService.auctionEventCallback.subscribe(
      ( response ) => {
        const auctionEvent = response;
        if (auctionEvent) {
          this.originalAuctionEvent = auctionEvent;
        }
        this.auctionEventServiceSubscription.unsubscribe();
      });
  }

  public subscribeAuctionEventList() {
    this.rbAuctionEventListServiceSubscription = this.eventService.rbAuctionEventListCallback.subscribe(
      (events) => {
        const rbAuctionEvents = events;
        this.categorizeAuctionEvents(rbAuctionEvents);
        this.rbAuctionEventListServiceSubscription.unsubscribe();
        this.loadingEvents = false;
      });
  }

  public subscribeAuctionEventChange() {
    this.filteredAuctionEvent = this.auctionEventControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filterAuctionEventList(value))
      );
    this.auctionEventControlSubscription = this.auctionEventControl.valueChanges.subscribe(
      auctionEvent => {
        this.onAuctionEventChange(auctionEvent);
      }
    );
  }

  public subscribeSiteChange() {
    this.filteredSiteList = this.siteControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filteredRbaSiteList(value))
      );
    this.siteListControlSubscription = this.siteControl.valueChanges.subscribe(
      site => {
        if  (site){
          this.onSiteChange(site.id);
        }
        this.checkMissingLocation();
      });
  }

  public subscribeLocationChange() {
    this.filteredLocationList = this.locationControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filteredLocations(value))
      );
    this.locationControlSubscription = this.locationControl.valueChanges.subscribe(
      location => {
        if (location) {
          this.onLocationChange(location.id);
        }
      });
  }

  public subscribeAppraisalLocationChange() {
    this.filteredAppraisalLocationList = this.appraisalLocationControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filteredLocations(value))
      );
    this.appraisalLocationControlSubscription = this.appraisalLocationControl.valueChanges.subscribe(
      location => {
        if ( location ) {
          this.onAppraisalLocationChange(location.id);
        }
      });
  }

  public subscribeSaleTypeChange() {
    this.filteredSaleTypeList = merge(this.reloadSaleTypes$, this.saleTypeControl.valueChanges).pipe(
      startWith(''),
      takeUntil(this.destroy$),
      map( value => this.filterSaleType(value))
    );
    this.saleTypeControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(saleType => this.onSaleTypeChange());
  }

  public subscribePlatformTypeChange() {
    this.filteredPlatformTypeList = merge(this.reloadPlatforms$, this.saleTypeChanged$, this.platformTypeControl.valueChanges)
     .pipe(
      startWith(''),
      takeUntil(this.destroy$),
      map(value => this.filterPlatformList(value)));

  }

  public unsubscribeAuctionEventChange() {
    if (this.auctionEventControlSubscription) {
      this.auctionEventControlSubscription.unsubscribe();
    }
  }

  public unsubscribeSiteChange() {
    if (this.siteListControlSubscription) {
      this.siteListControlSubscription.unsubscribe();
    }
  }

  public unsubscribeLocationChange() {
    if ( this.locationControlSubscription ) {
      this.locationControlSubscription.unsubscribe();
    }
  }

  public unsubscribeAppraisalLocationChange() {
    if ( this.appraisalLocationControlSubscription ) {
      this.appraisalLocationControlSubscription.unsubscribe();
    }
  }

  public setAuctionEventValue(auctionEvent: AuctionEvent) {
    this.unsubscribeAuctionEventChange();
    if (auctionEvent) {
      this.auctionEventControl.setValue(auctionEvent);
      this.assetSaleInfo.auctionEventId = auctionEvent.auctionEventId;
      this.assetSaleInfo.auctionEventLocationType = auctionEvent.locationType;
      this.subscribeAuctionEventChange();
      //this.assetSaleInfo.isAuctionSite = !(auctionEvent.locationType && auctionEvent.locationType === 'Offsite');
      if (this.assetSaleInfo.isAuctionSite) {
        if (this.siteList) {
          let filteredSites = this.siteList.filter(site => site.id === auctionEvent.siteId);
          this.setSiteValue(filteredSites[0]);
          if (auctionEvent && filteredSites[0]) {
            this.assetSaleInfo.offSite = !this.checkIfAssetIsOffSite(filteredSites[0].address, auctionEvent.eventLocationContactDto, null);
          }
        }
      } else {
        if (this.assetSaleInfo.locationId) {
          this.onLocationChange( this.assetSaleInfo.locationId );
        }
      }

      if (auctionEvent.localEventEndDateTime) {
        this.yardDeliveryMaxAllowedDate = new Date(auctionEvent.localEventEndDateTime);
      }
    } else {
      this.auctionEventControl.setValue(undefined);
      this.assetSaleInfo.auctionEventId = null;
      this.selectedAuctionEvent = null;
      this.subscribeAuctionEventChange();
      this.setSiteValue(null);
      this.assetSaleInfo.yardDeliveyDate = null;
    }
  }

  public clearSiteInput() {
    this.setSiteValue('');
  }

  public cleanAddressInput() {
    this.setAddressValue('');
  }

  public cleanAppraisalAddressInput() {
    this.setAppraisalAddressValue('');
    this.assetSaleInfo.appraisalLocationId = null;
    this.assetSaleInfo.appraisalLocationName = null;
    this.assetSaleInfo.newAppraisalSellerLocation = null;
  }

  public getSites() {
    if (this.siteListService.getSites().length > 0) {
      this.loadSites(this.siteListService.getSites());
    } else {
      this.loadingSites = true;
      this.siteListService.getSiteList();
    }
  }

  public getRbAuctionEvents() {
    if (this.eventService.data.rbAuctionEvents.length > 0) {
      this.categorizeAuctionEvents( this.eventService.data.rbAuctionEvents );
    } else {
      this.loadingEvents = true;
      if (this.assetSaleInfo.auctionEventId) {
        this.eventService.getAuctionEventDetails(this.assetSaleInfo.auctionEventId);
      }
      this.eventService.getRbAuctionEvents(60);
      if (this.eventService.data.rbAuctionEvents.length > 0) {
        this.categorizeAuctionEvents( this.eventService.data.rbAuctionEvents );
      }
    }
  }

  setSiteValue(value: any) {
    this.unsubscribeSiteChange();
    this.siteControl.setValue(value);
    if (value === '') {
      this.assetSaleInfo.siteId = null;
    }
    this.checkMissingLocation();
    this.subscribeSiteChange();
  }

  setAddressValue(value: any) {
    this.unsubscribeLocationChange();
    if (typeof value == 'number') {
      if (this.assetControlGroupLocations) {
        let location = this.assetControlGroupLocations.filter(location => location.auctionEventId && location.auctionEventId === value)[0];
        this.locationControl.setValue(location);
      }
    } else {
      this.locationControl.setValue(value);
    }
    this.assetSaleInfo.newSellerLocation=this.locationControl.value;
    if(this.assetSaleInfo.saleType !== RBA_LIVE) {
      if (this.assetSaleInfo.newSellerLocation && this.checkContactInfo(this.assetSaleInfo.newSellerLocation.inspContact) &&
        this.checkContactInfo(this.assetSaleInfo.newSellerLocation.transportContact)) {
        this.assetSaleInfo.isMissingContact = false;
      } else {
        this.assetSaleInfo.isMissingContact = true;
      }
    }
    this.checkMissingLocation();
    this.subscribeLocationChange();
  }

  setAppraisalAddressValue(value: any) {
    this.unsubscribeAppraisalLocationChange();
    if(typeof value == 'number') {
      if(this.assetControlGroupLocations){
        let location = this.assetControlGroupLocations.filter(location => location.auctionEventId && location.auctionEventId === value)[0];
        this.appraisalLocationControl.setValue(location);
      }
    } else {
      this.appraisalLocationControl.setValue(value);
    }
    this.assetSaleInfo.newAppraisalSellerLocation=this.appraisalLocationControl.value;
    this.subscribeAppraisalLocationChange();
  }

  public loadSites(sites: Site[]): void {
    this.siteList = sites;
    this.siteList.forEach(site => {
      site.cityProvinceCountry = site.address.cityProvinceCountry;
      if (this.assetSaleInfo.siteId && this.assetSaleInfo.siteId === site.id) {
        this.setSiteValue(site);
        const siteLocation = new SellerLocation();
        siteLocation.addressDto = site.address;
        siteLocation.siteId = site.id;
        siteLocation.locationDesc = site.name;
        this.assetSaleInfo.siteLocation = siteLocation;
        if (this.auctionEventControl.value && this.assetSaleInfo.auctionEventId && this.rbAuctionEvents) {
          let filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId);
          if (filteredEvents && filteredEvents.length > 0) {
            let eventLocation =  filteredEvents[0].eventLocationContactDto;
            this.assetSaleInfo.offSite = !this.checkIfAssetIsOffSite(site.address, eventLocation, null);
          }
        }
      }
    });
    this.assetSaleInfo.siteList = this.siteList;
  }

  public loadSaleInfo(): void {
    if (this.assetSaleInfo) {
      this.getSites();
      this.contact = new Contact();
      this.assetSaleInfo.yardDeliveyDate = this.assetSaleInfo.yardDeliveyDate ? new Date(this.assetSaleInfo.yardDeliveyDate) : new Date();
      this.auctionEventControl.markAsTouched();
      this.updateBuyNowPercent(this.assetSaleInfo.strikePriceAmt);

      if (this.assetSaleInfo.assetControlGroupId > 0) {
        this.opportunityCurrency = this.assetSaleInfo.opportunityCurrency;
        this.initSaleTypeDropdown(this.assetSaleInfo.opportunityId);
        this.getRbAuctionEvents();
        this.assetControlGroupService.getLocationsByAssetControlGroup(this.assetSaleInfo.assetControlGroupId);
      }
    }
  }

  public loadPagePermission() {
    this.canEditInspectionType = !this.assetSaleInfo.isPostContract || (this.canUpdateAssetSalesInfo && this.assetSaleInfo.equipmentStatus <= equipmentState.inspectionPerformed);
    this.canEditPrices = !this.assetSaleInfo.isPostContract ||
                    ((this.canUpdateAssetSalesInfo || !this.assetSaleInfo.isValidSalePrices()) && this.assetSaleInfo.equipmentStatus < equipmentState.preview);

    this.canEditShootPrice = !this.assetSaleInfo.isPostContract || (this.canUpdateAssetSalesInfo &&((this.assetSaleInfo.equipmentStatus < equipmentState.sold && this.assetSaleInfo.saleType !== RBA_LIVE)
      || (!this.isAtRiskContract && this.isEquipSaleInfoFieldsEditable)));

    this.canEditYardDeliverydate = !this.assetSaleInfo.isPostContract || (this.canUpdateAssetSalesInfo && this.isEquipSaleInfoFieldsEditable);

    this.isVSOEditable = !this.assetSaleInfo.isPostContract || (this.assetSaleInfo.isPostContract && this.isVSOEvent) || (this.assetSaleInfo.saleType !== RBA_LIVE) ;
    this.canEditStorageLocation = !this.assetSaleInfo.isPostContract ||
      (this.isVSOEditable || this.isEquipSaleInfoFieldsEditable) && this.canUpdateAssetSalesInfo;
  }


  public hasReachedContractReady(): boolean {
    return this.assetSaleInfo.assetStatusId >= ASSET_STATUS_CONTRACT_READY;
  }

  public checkMissingLocation() {
    this.assetSaleInfo.isMissingLocation = !this.assetSaleInfo.addNewAddress &&
      ((!this.assetSaleInfo.isAuctionSite && !this.locationControl.value) ||
        (this.assetSaleInfo.isAuctionSite && !this.siteControl.value))
    ;
  }

  public disableFormControls() {
    if (this.assetSaleInfo.isPostContract) {
      if (this.auctionEventControl) {
        this.auctionEventControl.disable({emitEvent: false});
      }
      if (this.siteControl) {
        this.siteControl.disable({emitEvent: false});
      }
      if (this.locationControl) {
        this.locationControl.disable({emitEvent: false});
      }
      if (this.appraisalLocationControl) {
        this.appraisalLocationControl.disable({emitEvent: false});
      }
    }
  }

  public calculateYardDeliveryDate( auctionEvent: AuctionEvent ): void {
    if (auctionEvent) {
      if (!this.eventAlreadyStarted(auctionEvent)) {
        const eventStartDate = new Date(auctionEvent.localEventStartDateTime);
        const deadLine = new Date();
        deadLine.setDate(( deadLine.getDate()) + 15 );
        if (eventStartDate.getTime() >=  deadLine.getTime()) {
          const calculatedDate = new Date(auctionEvent.localEventStartDateTime);
          calculatedDate.setDate( ( calculatedDate.getDate() - 15) );
          this.assetSaleInfo.yardDeliveyDate = calculatedDate;
        } else {
          this.assetSaleInfo.yardDeliveyDate = new Date();
        }
      } else {
        this.assetSaleInfo.yardDeliveyDate = new Date(auctionEvent.localEventStartDateTime);
      }
    }
  }

  public validateYardDeliveryDate( yardDeliveryMomentDate: Moment ): void {
    const yardDeliveryDate = yardDeliveryMomentDate ? yardDeliveryMomentDate.toDate() : null;
    if (this.assetSaleInfo.auctionEventId && yardDeliveryDate) {
      const filteredEvents = this.rbAuctionEvents.filter(auctionEvent => auctionEvent.auctionEventId == this.assetSaleInfo.auctionEventId );
      if (filteredEvents && filteredEvents.length > 0) {
        const currentAuctionEvent: AuctionEvent = filteredEvents[0];
        if (!this.eventAlreadyStarted(currentAuctionEvent)) {
          const eventStartDate = new Date(currentAuctionEvent.localEventStartDateTime);
          const deadLine = new Date();
          deadLine.setTime(eventStartDate.getTime());
          deadLine.setDate( deadLine.getDate() - 15 );
          if (yardDeliveryDate.getTime() > deadLine.getTime()) {
            const yardDeliveryDateWarning =  appMessage.yardDeliveryDateWarning;
            this.dialogService.showMessageDialog(yardDeliveryDateWarning);
          }
        }
      }
    }
  }

  public eventAlreadyStarted(auctionEvent: AuctionEvent): boolean {
    return auctionEvent.localEventStartDateTime < new Date().getTime();
  }

  private subscribeMoveIndicator() {
    this.moveIndicatorSubscription = this.moveIndicatorControlService.announced$.subscribe( (elementName) => {
      this.moveToElement(elementName);
    });
  }

  private moveToElement(elementName : string){
    const SCROLL_WINDOW_OPTIONS_CENTER = { behavior: 'smooth', block: 'center', inline: 'start' };
    switch(elementName){
      case 'saleType':
        if (this.saleTypeElement) {
          this.saleTypeElement.nativeElement.scrollIntoView(SCROLL_WINDOW_OPTIONS_CENTER);
        }
        break;
      case 'auctionEvent':
        if (this.auctionEventElement) {
          this.auctionEventElement.nativeElement.scrollIntoView(SCROLL_WINDOW_OPTIONS_CENTER);
        }
        break;
      case 'storageLocation':
        if (this.storageLocationElement) {
          this.storageLocationElement.nativeElement.scrollIntoView(SCROLL_WINDOW_OPTIONS_CENTER);
        }
        break;
      case 'reservePrice':
        if (this.reservePriceElement) {
          this.reservePriceElement.nativeElement.scrollIntoView(SCROLL_WINDOW_OPTIONS_CENTER);
        }
    }
  }

  private checkOffSiteLocation() : void {
    if  (this.assetSaleInfo.saleType === RBA_LIVE) {
      if (this.assetSaleInfo.isAuctionSite) {
        this.siteControl.updateValueAndValidity({onlySelf: true, emitEvent: true});
      } else {
        this.locationControl.updateValueAndValidity({onlySelf: true, emitEvent: true});
      }
    }
  }

  /**
   * This method will prevent the decimal numbers
   * @param event
   */
  public preventDecimalValue(event, ngModel) {
    this.validNumericValue = (event.key === '.') ? ngModel : null;
    if (event.key === '.') {
      event.preventDefault();
    }
  }

  public isValidAddress() : boolean {
    if (this.assetSaleInfo.addNewAddress && this.addressForm) {
      return this.addressForm.isValidAddress();
    }
    return true;
  }

  public isValidAppraisalAddress() : boolean {
    if ( this.assetSaleInfo.addNewAppraisalAddress && this.appraisalAddressForm ){
      return this.appraisalAddressForm.isValidAddress();
    }
    return true;
  }

  /**
   * This method will find out the whether asset status is in contract ready or work in progress
   */
  public isWIPorContractReady() : boolean {
    if (this.assetSaleInfo && (this.assetSaleInfo.assetStatusId === ASSET_STATUS_CONTRACT_READY
      || this.assetSaleInfo.assetStatusId === ASSET_STATUS_CONTRACTED
      || this.assetSaleInfo.assetStatusId === ASSET_STATUS_INFO_COMPLETE )) {
      return true;
    }
    return false;
  }

  isAddContactInformation(location: SellerLocation, isNewAddress: boolean): boolean {
    if (!location.inspContact && !location.altInspContact && !location.transportContact && !isNewAddress) {
      return true;
    } else {
      return false;
    }
  }

  isEditContactInformation(location: SellerLocation): boolean {
    if (location.inspContact || location.altInspContact || location.transportContact) {
      return true;
    } else {
      return false;
    }
  }

  addOrEditContactInformation() {
    this.showAddContactInformation(this.assetSaleInfo.newSellerLocation)
      .afterClosed()
      .subscribe((assetLocation) => {
        if (assetLocation) {
          this.assetSaleInfo.newSellerLocation = assetLocation;
          if(this.assetSaleInfo.saleType !== RBA_LIVE) {
          this.isInspecSchedule = this.checkContactInfo(assetLocation.inspContact);
          this.isPickUp = this.checkContactInfo(assetLocation.transportContact);
            if (this.isInspecSchedule && this.isPickUp) {
              this.assetSaleInfo.isMissingContact = false;
            } else {
              this.assetSaleInfo.isMissingContact = true;
            }
          }
        }
      });
  }

  addOrEditAppraisalContactInformation() {
    this.showAddContactInformation(this.assetSaleInfo.newAppraisalSellerLocation)
      .afterClosed()
      .subscribe((assetLocation) => {
        if (assetLocation) {
          this.assetSaleInfo.newAppraisalSellerLocation = assetLocation;
        }
      });
  }

  addContactInformationforNewStorage() {
    this.isEditingContactAndAddress = false;
    this.showAddContactInformation(this.assetSaleInfo.newLocation)
      .afterClosed()
      .subscribe((assetLocation) => {
        if (assetLocation) {
          this.assetSaleInfo.newLocation = assetLocation;
          if(this.assetSaleInfo.saleType !== RBA_LIVE) {
            this.isInspecSchedule = this.checkContactInfo(assetLocation.inspContact);
            this.isPickUp = this.checkContactInfo(assetLocation.transportContact);
            if (this.isInspecSchedule && this.isPickUp) {
              this.assetSaleInfo.isMissingContact = false;
            } else {
              this.assetSaleInfo.isMissingContact = true;
            }
          }
          this.address=assetLocation.addressDto;
          this.isEditingContactAndAddress = true;
        }
      });
  }

  addContactInformationForNewAppraisalLocation() {
    this.isEditingAppraisalContactAndAddress = false;
    this.showAddContactInformation(this.assetSaleInfo.newAppraisalLocation)
      .afterClosed()
      .subscribe((assetLocation) => {
        if (assetLocation) {
          this.assetSaleInfo.newAppraisalLocation = assetLocation;
          this.appraisalAddress=assetLocation.addressDto;
          this.isEditingAppraisalContactAndAddress = true;
        }
      });
  }

  showAddContactInformation(location:SellerLocation): MatDialogRef<StorageLocationComponent>{
    return this.dialog
      .open(StorageLocationComponent, {
        panelClass: 'mat-panel--full-height',
        data: {
          location: location,
          addContactInfoFlag: true,
          enableSaveLocationOnModelSave: this.enableSaveLocationOnModelSave && !!location.id,
        }
      });
  }

  public enableFormControl(formControl: FormControl): FormControl{
    if(this.canUpdateAssetSalesInfo && ((this.assetSaleInfo.isPostContract && this.isVSOEvent) || this.isEquipSaleInfoFieldsEditable)){
      formControl.enable({emitEvent: false})
    }
    return formControl;
  }

  public isIPInspectionCompleteForVSOEvent():boolean{
    let isIPInspection : boolean = false;
    for( let inspectionType of ipAssetInspectionTypes){
      if (this.assetSaleInfo.inspectionType === inspectionType.code) {
        isIPInspection = true;
        break;
      }
    }
    return  this.isVSOEvent && (this.assetSaleInfo.equipmentStatus >= equipmentState.inspectionPerformed ) && isIPInspection;
  }

  isContactInfo(contactInfo): boolean {
    for (let key in contactInfo){
      return contactInfo[key] ? true : false;
    }
  }

  checkContactInfo(contactInfo): boolean {
    if (this.assetSaleInfo.saleType !== RBA_LIVE) {
      if (contactInfo && contactInfo.firstName && contactInfo.lastName && contactInfo.voicePhoneNumber && contactInfo.email) {
        return true
      }
      return false;
    } else {
      return true;
    }
  }
  public getCurrencyPlaceHolder(): string {
    return getCurrencyCodeAndSymbol(this.assetSaleInfo?.opportunityCurrency);
  }

  public isCurrencyPrefixVisible(theValue: any): boolean {
    return theValue != null && theValue !== '';
  }
}
